import React, { useState, useEffect } from 'react';
import { useTranslation } from '../hooks/useTranslation';
import ProductCard from './common/ProductCard';
import { ProductCardSkeleton } from './common/Skeleton';
import { getAdImageUrl, getAdImageUrls } from '../utils/adImageUrl';
import { getInterestGameNameKeys, pickPersonalizedFeatured } from '../utils/featuredPersonalization';
import '../styles/FeaturedProducts.css';
import '../styles/CatalogPage.css';

const PERSONALIZED_FETCH_POOL = 96;

const FeaturedProducts = ({ maxAds = null, hideOnMobile = false, variant = 'default' }) => {
  const isHomeLayout = variant === 'home';
  const { t } = useTranslation();
  const [activeFilter, setActiveFilter] = useState('NEW ITEMS'); // 'NEW ITEMS' | 'MOST VIEWED' | 'SEE ALL'
  const [selectedProduct, setSelectedProduct] = useState(null);
  const cacheScope = maxAds != null ? String(maxAds) : 'full';

  // Load from cache immediately for instant display (skipped when maxAds — fresh shuffle each visit)
  const getCachedProducts = (filter) => {
    if (maxAds != null) return null;
    if (typeof localStorage !== 'undefined') {
      try {
        const cacheKey = `featured_products_${filter}_${cacheScope}`;
        const cached = localStorage.getItem(cacheKey);
        if (cached) {
          return JSON.parse(cached);
        }
      } catch (e) {
        // Ignore cache errors
      }
    }
    return null;
  };

  // Get cached products for current filter IMMEDIATELY (synchronous)
  const getInitialProducts = () => {
    const cached = getCachedProducts(activeFilter);
    return cached || [];
  };
  
  const initialProducts = maxAds != null ? [] : getInitialProducts();
  const [products, setProducts] = useState(initialProducts);
  const [loading, setLoading] = useState(maxAds != null ? true : initialProducts.length === 0);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(maxAds != null ? false : initialProducts.length > 0);

  // Map game names to their icon filenames
  const getGameIcon = (gameName) => {
    const iconMap = {
      'Counter Strike 2': 'CS2.png',
      'Fortnite': 'Fortnite.png',
      'Roblox': 'Roblox.png',
      'Valorant': 'Valorant.png',
      'Warcraft': 'Worldofwarcraft.png',
      'COD Call of Duty': 'Callofduty.png',
      'Call of Duty': 'Callofduty.png'
    };
    
    if (gameName === '8 Ball Pool') {
      return '/main/games/icons/8bp.webp';
    }
    if (gameName === 'Kaiju No. 8') {
      return '/main/games/icons/kaiju8.jpg';
    }
    if (gameName === '99 Nights in the Forest') {
      return '/main/games/icons/99nitf.webp';
    }
    if (gameName === 'Adopt Me') {
      return '/main/games/icons/adoptme.jpeg';
    }
    if (gameName === 'Albion Online') {
      return '/main/games/icons/alibiononline.webp';
    }
    if (gameName === 'All Star Tower Defense X') {
      return '/main/games/icons/allstartowerdefense.jpeg';
    }
    if (gameName === 'Animal Crossing: New Horizons') {
      return '/main/games/icons/animalcrossing.jpg';
    }
    if (gameName === 'Anime Defenders') {
      return '/main/games/icons/animedefenders.webp';
    }
    if (gameName === 'Anime Vanguards') {
      return '/main/games/icons/animevanguards.jpg';
    }
    if (gameName === 'ARK: Survival Ascended') {
      return '/main/games/icons/arksurvivalascended.webp';
    }
    if (gameName === 'Arknights') {
      return '/main/games/icons/Arknights.png';
    }
    if (gameName === 'Ayakashi Rumble!') {
      return '/main/games/icons/AyakashiRumble.webp';
    }
    if (gameName === 'Battlenet') {
      return '/main/games/icons/battlenet.jpg';
    }
    if (gameName === 'Arena Breakout') {
      return '/main/games/icons/arenabreakout1.jpg';
    }
    if (gameName === 'Arena Breakout: Infinite') {
      return '/main/games/icons/arenabreakout.jpg';
    }
    if (gameName === 'Battlefield') {
      return '/main/games/icons/battlefield6.webp';
    }
    if (gameName === 'Black Clover M') {
      return '/main/games/icons/Blackclover.jpeg';
    }
    if (gameName === 'Black Desert Online') {
      return '/main/games/icons/blackdessertonline.png';
    }
    if (gameName === 'Bleach: Brave Souls') {
      return '/main/games/icons/Bleachbravesouls.webp';
    }
    if (gameName === 'Blood Strike') {
      return '/main/games/icons/bloodstrike.webp';
    }
    if (gameName === 'Blox Fruits') {
      return '/main/games/icons/bloxfruits.jpg';
    }
    if (gameName === 'Brawl Stars') {
      return '/main/games/icons/brawl-stars.webp';
    }
    if (gameName === 'Call of Duty Mobile') {
      return '/main/games/icons/codmobile.webp';
    }
    if (gameName === 'Car Parking Multiplayer') {
      return '/main/games/icons/carparkingmultiplayer.webp';
    }
    if (gameName === 'Chaos Zero Nightmare') {
      return '/main/games/icons/choaszero.webp';
    }
    if (gameName === 'Chocotto Land') {
      return '/main/games/icons/chocottoland.jpg';
    }
    if (gameName === 'Clash of Clans') {
      return '/main/games/icons/clashofclans.jpg';
    }
    if (gameName === 'Cookie Run: Kingdom') {
      return '/main/games/icons/cookierunkingdom.webp';
    }
    if (gameName === 'Crunchyroll') {
      return '/main/games/icons/crunchyroll.jpg';
    }
    if (gameName === 'Dark and Darker') {
      return '/main/games/icons/darkanddarker.jpg';
    }
    if (gameName === 'DarkOrbit') {
      return '/main/games/icons/darkorbit.jpg';
    }
    if (gameName === 'DayZ') {
      return '/main/games/icons/dayz.avif';
    }
    if (gameName === 'Dead By Daylight') {
      return '/main/games/icons/deadbydaylight.jpg';
    }
    if (gameName === 'Dead Rails') {
      return '/main/games/icons/deadrails.jpg';
    }
    if (gameName === 'Delta Force') {
      return '/main/games/icons/deltaforce.jpeg';
    }
    if (gameName === 'Destiny 2') {
      return '/main/games/icons/destiny2.jpeg';
    }
    if (gameName === 'Diablo 4') {
      return '/main/games/icons/diablo4.avif';
    }
    if (gameName === 'Diablo Immortal') {
      return '/main/games/icons/diabloimortal.jpg';
    }
    if (gameName === 'Digimon Super Rumble') {
      return '/main/games/icons/digimonsuperrumble.jpg';
    }
    if (gameName === 'Discord') {
      return '/main/games/icons/discord.jpg';
    }
    if (gameName === 'Dota 2') {
      return '/main/games/icons/dota2.jpg';
    }
    if (gameName === 'Dragon Ball Legends') {
      return '/main/games/icons/dragonballlegends.jpg';
    }
    if (gameName === 'Dragon Ball Z Dokkan Battle') {
      return '/main/games/icons/DragonBallZDokkanBattle.jpg';
    }
    if (gameName === 'Duet Night Abyss') {
      return '/main/games/icons/duetnightabyss.jpg';
    }
    if (gameName === 'EA Sports FC Mobile') {
      return '/main/games/icons/eafcmobile.jpg';
    }
    if (gameName === 'eFootball') {
      return '/main/games/icons/efotball.jpg';
    }
    if (gameName === 'Elden Ring') {
      return '/main/games/icons/eldenring.jpg';
    }
    if (gameName === 'Elder Scrolls Online') {
      return '/main/games/icons/elderscrollsonline.jpg';
    }
    if (gameName === 'Epic Games') {
      return '/main/games/icons/epicgames.jpg';
    }
    if (gameName === 'Epic Seven') {
      return '/main/games/icons/epicseven.jpg';
    }
    if (gameName === 'Etheria: Restart') {
      return '/main/games/icons/etheriarestart.jpg';
    }
    if (gameName === 'Fallout 76') {
      return '/main/games/icons/fallout76.jpg';
    }
    if (gameName === 'Fate/Grand Order') {
      return '/main/games/icons/fategrandorder.jpg';
    }
    if (gameName === 'Final Fantasy XIV') {
      return '/main/games/icons/finalfantasyxix.jpg';
    }
    if (gameName === 'Fisch') {
      return '/main/games/icons/fisch.jpg';
    }
    if (gameName === 'Forza Horizon 5') {
      return '/main/games/icons/forzahorizon5.jpg';
    }
    if (gameName === 'Garena Free Fire') {
      return '/main/games/icons/garenafreefire.jpeg';
    }
    if (gameName === 'Genshin Impact') {
      return '/main/games/icons/genshinimpact.avif';
    }
    if (gameName === 'Girls Frontline 2: Exilium') {
      return '/main/games/icons/girlsfrontline2exilium.jpg';
    }
    if (gameName === 'Golden HoYeah') {
      return '/main/games/icons/goldenhoyeah.png';
    }
    if (gameName === 'Granblue Fantasy') {
      return '/main/games/icons/granbluefantasy.webp';
    }
    if (gameName === 'Grand Roleplay') {
      return '/main/games/icons/grandrp.jpg';
    }
    if (gameName === 'Grand Theft Auto 5') {
      return '/main/games/icons/grandtheftauto.jpg';
    }
    if (gameName === 'Grow a Garden') {
      return '/main/games/icons/growagarden.jpg';
    }
    if (gameName === 'Hay Day') {
      return '/main/games/icons/hayday.jpg';
    }
    if (gameName === 'Honkai Impact 3rd') {
      return '/main/games/icons/honkaimpact3rd.jpg';
    }
    if (gameName === 'Honkai: Star Rail') {
      return '/main/games/icons/honkaistarrail.jpg';
    }
    if (gameName === 'Houchi Shoujo') {
      return '/main/games/icons/houchishouju.jpeg';
    }
    if (gameName === 'Identity V') {
      return '/main/games/icons/identityv.jpg';
    }
    if (gameName === 'Jailbreak') {
      return '/main/games/icons/jailbreak.webp';
    }
    if (gameName === "JoJo's Bizarre Adventure: Ora Ora Overdrive") {
      return '/main/games/icons/jojoOraOra.webp';
    }
    if (gameName === 'Jujutsu Infinite') {
      return '/main/games/icons/jujutsuinfinite.jpeg';
    }
    if (gameName === 'Jujutsu Kaisen Phantom Parade') {
      return '/main/games/icons/JujutsuKaisenPhantomParade.jpeg';
    }
    if (gameName === 'King Legacy') {
      return '/main/games/icons/kinglegacy.jpg';
    }
    if (gameName === 'Knight Online') {
      return '/main/games/icons/knightonline.jpg';
    }
    if (gameName === 'Knives Out') {
      return '/main/games/icons/knivesout.jpg';
    }
    if (gameName === 'Last Island of Survival') {
      return '/main/games/icons/lastislandofsurvival.avif';
    }
    if (gameName === 'Last War: Survival') {
      return '/main/games/icons/lastwarsurvival.jpg';
    }
    if (gameName === 'League of Legends') {
      return '/main/games/icons/leagueoflegends.jpg';
    }
    if (gameName === 'League of Legends: Wild Rift') {
      return '/main/games/icons/leagueoflegendswildrift.jpg';
    }
    if (gameName === 'Livly Island') {
      return '/main/games/icons/livlyisland.png';
    }
    if (gameName === 'Lords Mobile') {
      return '/main/games/icons/lordsmobile.jpg';
    }
    if (gameName === 'Lost Ark') {
      return '/main/games/icons/lostark.jpg';
    }
    if (gameName === 'Madoka Magica Magia Exedra') {
      return '/main/games/icons/MadokaMagicaMagiaExedra.jpg';
    }
    if (gameName === 'Marvel Contest of Champions') {
      return '/main/games/icons/marvelcontestofchampions.jpg';
    }
    if (gameName === 'Marvel Rivals') {
      return '/main/games/icons/marvelrivals.jpg';
    }
    if (gameName === 'Metin2') {
      return '/main/games/icons/metin2.jpg';
    }
    if (gameName === 'Millennium War Aigis') {
      return '/main/games/icons/MillenniumWarAigis.webp';
    }
    if (gameName === 'Mobile Legends') {
      return '/main/games/icons/mobilelegends.webp';
    }
    if (gameName === 'Monster Hunter Now') {
      return '/main/games/icons/MonsterHunterNow.jpeg';
    }
    if (gameName === 'Monster Strike') {
      return '/main/games/icons/monsterstrike.jpg';
    }
    if (gameName === 'Netflix') {
      return '/main/games/icons/netflix.jpg';
    }
    if (gameName === 'New World') {
      return '/main/games/icons/newworld.jpg';
    }
    if (gameName === 'One Piece Bounty Rush') {
      return '/main/games/icons/OnePieceBountyRush.jpg';
    }
    if (gameName === 'One Piece Treasure Cruise') {
      return '/main/games/icons/OnePieceTreasureCruise.jpg';
    }
    if (gameName === 'Origin') {
      return '/main/games/icons/orgin.jpg';
    }
    if (gameName === 'Overwatch 2') {
      return '/main/games/icons/overwatch2.jpg';
    }
    if (gameName === 'Path of Exile') {
      return '/main/games/icons/pathofexile.jpg';
    }
    if (gameName === 'Path of Exile 2') {
      return '/main/games/icons/pathofexile2.webp';
    }
    if (gameName === 'Persona 5: The Phantom X') {
      return '/main/games/icons/Persona5ThePhantomX.jpg';
    }
    if (gameName === 'Plants vs Brainrots') {
      return '/main/games/icons/PlantsvsBrainrots.jpg';
    }
    if (gameName === 'PlayStation') {
      return '/main/games/icons/playstation.jpg';
    }
    if (gameName === 'Pokecolo') {
      return '/main/games/icons/pokecolo.jpg';
    }
    if (gameName === 'Pokecolo Twin') {
      return '/main/games/icons/pokecolotwin.jpg';
    }
    if (gameName === 'Pokemon Go') {
      return '/main/games/icons/pokemongo.jpg';
    }
    if (gameName === 'Pokemon TCG Pocket') {
      return '/main/games/icons/pokemontcgpocket.jpg';
    }
    if (gameName === 'Princess Connect! Re:Dive') {
      return '/main/games/icons/PrincessConnectRe-Dive.png';
    }
    if (gameName === 'Professional Baseball Spirits A') {
      return '/main/games/icons/ProfessionalBaseballSpiritsA.jpg';
    }
    if (gameName === 'Project SEKAI') {
      return '/main/games/icons/projectsekai.webp';
    }
    if (gameName === 'PUBG') {
      return '/main/games/icons/pubg.jpg';
    }
    if (gameName === 'PUBG Mobile') {
      return '/main/games/icons/pubgmobile.jpg';
    }
    if (gameName === 'Puzzle and Dragons') {
      return '/main/games/icons/PuzzleandDragons.webp';
    }
    if (gameName === 'Raid: Shadow Legends') {
      return '/main/games/icons/raidshadowlegends.jpg';
    }
    if (gameName === 'Rainbow Six Siege X') {
      return '/main/games/icons/RainbowSixSiegeX.jpg';
    }
    if (gameName === 'Rise of Kingdoms') {
      return '/main/games/icons/RiseofKingdoms.jpg';
    }
    if (gameName === 'Rocket League') {
      return '/main/games/icons/rocketleague.jpg';
    }
    if (gameName === 'Royale Online') {
      return '/main/games/icons/royaleonline.jpg';
    }
    if (gameName === 'RuneScape 3') {
      return '/main/games/icons/runescape3.jpg';
    }
    if (gameName === 'Rust') {
      return '/main/games/icons/rust.png';
    }
    if (gameName === 'SD Gundam G Generation ETERNAL') {
      return '/main/games/icons/eternal.webp';
    }
    if (gameName === 'Sea of Thieves') {
      return '/main/games/icons/seaofthieves.jpg';
    }
    if (gameName === 'Seven Knights Re:BIRTH') {
      return '/main/games/icons/SevenKnightsReBIRTH.jpg';
    }
    if (gameName === 'Shadowverse') {
      return '/main/games/icons/shadowverse.jpg';
    }
    if (gameName === 'Silver and Blood') {
      return '/main/games/icons/silverandblood.webp';
    }
    if (gameName === 'Sky: Children of the Light') {
      return '/main/games/icons/SkyChildrenoftheLight.jpg';
    }
    if (gameName === 'Spotify') {
      return '/main/games/icons/spotify.jpg';
    }
    if (gameName === 'Standoff 2') {
      return '/main/games/icons/standoff2.jpg';
    }
    if (gameName === 'Steal a Brainrot') {
      return '/main/games/icons/stealabrainrot.jpg';
    }
    if (gameName === 'Stella Sora') {
      return '/main/games/icons/stellasora.jpg';
    }
    if (gameName === 'Summoners War') {
      return '/main/games/icons/SummonersWar.jpg';
    }
    if (gameName === 'The Finals') {
      return '/main/games/icons/thefinals.jpg';
    }
    if (gameName === 'The Forge') {
      return '/main/games/icons/theforge.jpg';
    }
    if (gameName === 'The Seven Deadly Sins') {
      return '/main/games/icons/thesevendeadlysins.jpg';
    }
    if (gameName === 'Throne and Liberty') {
      return '/main/games/icons/ThroneandLiberty.jpg';
    }
    if (gameName === 'Tower of Fantasy') {
      return '/main/games/icons/toweroffantasy.jpg';
    }
    if (gameName === 'Ubisoft Connect') {
      return '/main/games/icons/ubisoftconnect.jpeg';
    }
    if (gameName === 'Uma Musume Pretty Derby') {
      return '/main/games/icons/UmaMusumePrettyDerby.jpg';
    }
    if (gameName === 'unVEIL the world') {
      return '/main/games/icons/unVEILtheworld.jpeg';
    }
    if (gameName === 'Volleyball Legends') {
      return '/main/games/icons/volleyballlegends.webp';
    }
    if (gameName === 'War Thunder') {
      return '/main/games/icons/warthunder.jpg';
    }
    if (gameName === 'Warframe') {
      return '/main/games/icons/warframe.jpg';
    }
    if (gameName === 'Wartune Ultra') {
      return '/main/games/icons/wartuneultra.png';
    }
    if (gameName === 'Watcher of Realms') {
      return '/main/games/icons/watcherofrealms.jpg';
    }
    if (gameName === 'Where Winds Meet') {
      return '/main/games/icons/WhereWindsMeet.jpg';
    }
    if (gameName === 'White Cat Project') {
      return '/main/games/icons/WhiteCatProject.jpg';
    }
    if (gameName === 'Whiteout Survival') {
      return '/main/games/icons/whiteoutsurvial.jpg';
    }
    if (gameName === 'WolfTeam') {
      return '/main/games/icons/wolfteam.jpg';
    }
    if (gameName === 'World of Tanks') {
      return '/main/games/icons/worldoftanks.jpg';
    }
    if (gameName === 'World of Warcraft') {
      return '/main/games/icons/worldofwarcraft.jpg';
    }
    if (gameName === 'World of Warships') {
      return '/main/games/icons/worldofwarships.jpg';
    }
    if (gameName === 'WoW Classic') {
      return '/main/games/icons/wowclassic.jpg';
    }
    if (gameName === 'WoW Mists of Pandaria') {
      return '/main/games/icons/WoWMistsofPandaria.jpg';
    }
    if (gameName === 'Wuthering Waves') {
      return '/main/games/icons/WutheringWaves.jpg';
    }
    if (gameName === 'Xbox') {
      return '/main/games/icons/xbox.jpg';
    }
    if (gameName === 'Yo-kai Watch: Wibble Wobble') {
      return '/main/games/icons/YokaiWatchWibbleWobble.jpg';
    }
    if (gameName === 'Yu-Gi-Oh! Master Duel') {
      return '/main/games/icons/Yu-Gi-OhMasterDuel.jpg';
    }
    if (gameName === 'Zenless Zone Zero') {
      return '/main/games/icons/ZenlessZoneZero.jpg';
    }
    if (gameName === 'Zula') {
      return '/main/games/icons/zula.jpeg';
    }
    if (gameName === 'Goddess of Victory: Nikke') {
      return '/main/games/icons/nikke.jpg';
    }
    
    return iconMap[gameName] ? `/main/hero section/promos/navbaricons/${iconMap[gameName]}` : null;
  };

  // Fetch featured ads - using same simple mechanism as useGameAds hook
  useEffect(() => {
    if (maxAds != null) {
      setProducts([]);
      setLoading(true);
      setHasLoadedOnce(false);
    } else {
      const currentCachedProducts = getCachedProducts(activeFilter);

      if (currentCachedProducts && currentCachedProducts.length > 0) {
        setProducts(currentCachedProducts);
        setLoading(false);
        setHasLoadedOnce(true);
      } else if (currentCachedProducts && currentCachedProducts.length === 0) {
        setProducts([]);
        setLoading(false);
        setHasLoadedOnce(true);
      } else {
        setLoading(true);
        setHasLoadedOnce(false);
      }
    }

    const fetchFeaturedAds = async () => {
      try {
        const limit =
          maxAds != null
            ? PERSONALIZED_FETCH_POOL
            : activeFilter === 'SEE ALL'
              ? 200
              : 50;
        const response = await fetch(`/api/ads/public?limit=${limit}`);
        const result = await response.json();

        if (result.data) {
          // Map ads to ProductCard format (same mapping logic as useGameAds)
          const mappedProducts = result.data.map(ad => {
            // Determine account type display
            let accountTypeDisplay = 'Product';
            if (ad.service_type === 'accounts' && ad.account_type) {
              accountTypeDisplay = ad.account_type;
            } else if (ad.service_type) {
              accountTypeDisplay = ad.service_type.charAt(0).toUpperCase() + ad.service_type.slice(1).replace('-', ' ');
            }

            // Get seller info
            const sellerUsername = ad.seller?.username || ad.seller_username || 'Seller';
            const sellerAvatar = ad.seller?.avatar_url || ad.seller_avatar_url || null;

            return {
              id: ad.id,
              sellerId: ad.seller_id,
              platform: ad.platform || 'PC',
              type: accountTypeDisplay,
              features: ad.title || 'Untitled Product',
              seller: sellerUsername,
              sellerAvatar: sellerAvatar && !sellerAvatar.startsWith('letter:') ? sellerAvatar : null,
              sellerInitial: sellerUsername.charAt(0).toUpperCase(),
              price: ad.price ? parseFloat(ad.price).toFixed(2) : '0.00',
              currency: ad.currency || 'EUR', // Include original currency for conversion
              rating: 99.9,
              reviews: 0,
              delivery: 'Instant',
              verified: true,
              image: ad.image_urls && Array.isArray(ad.image_urls) && ad.image_urls.length > 0 ? getAdImageUrl(ad.image_urls[0]) : null,
              images: getAdImageUrls(ad.image_urls || []),
              region: ad.region || 'EU',
              status: 'Available',
              game: ad.game,
              serviceType: ad.service_type,
            };
          });

          const shuffled = [...mappedProducts].sort(() => Math.random() - 0.5);

          const capped =
            maxAds != null
              ? pickPersonalizedFeatured(shuffled, maxAds, getInterestGameNameKeys())
              : shuffled;

          setProducts(capped);
          setHasLoadedOnce(true);
          setLoading(false);

          if (maxAds == null && typeof localStorage !== 'undefined') {
            const cacheKey = `featured_products_${activeFilter}_${cacheScope}`;
            localStorage.setItem(cacheKey, JSON.stringify(capped));
          }
        } else {
          setHasLoadedOnce(true);
          setLoading(false);
        }
      } catch (error) {
        // Silently fail - cached products will still be shown - same as useGameAds
        // Mark as loaded even on error so we don't show "No products found" flash
        setHasLoadedOnce(true);
        setLoading(false);
      }
    };

    // Fetch in background - don't block UI - same as useGameAds
    fetchFeaturedAds();
  }, [activeFilter, maxAds]);

  const games = ['Fortnite', 'Counter Strike 2', 'Roblox', 'Warcraft', 'Valorant'];

  const skeletonCount = maxAds != null ? Math.min(maxAds, 12) : 8;

  return (
    <section
      className={[
        'featured-products',
        maxAds != null ? 'featured-products--twelve' : '',
        hideOnMobile ? 'featured-products--hide-on-mobile' : '',
        isHomeLayout ? 'featured-products--home' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <div className="products-container">
        <div className="products-header">
          <h2 className="section-title">{isHomeLayout ? 'Recommendations' : t('products.featured')}</h2>
          <div className="filter-tabs">
            <button 
              className={activeFilter === 'NEW ITEMS' ? 'filter-tab active' : 'filter-tab'}
              onClick={() => setActiveFilter('NEW ITEMS')}
            >
              {isHomeLayout ? 'New' : t('products.newItems')}
            </button>
            <button 
              className={activeFilter === 'MOST VIEWED' ? 'filter-tab active' : 'filter-tab'}
              onClick={() => setActiveFilter('MOST VIEWED')}
            >
              {isHomeLayout ? 'Popular' : t('products.mostViewed')}
            </button>
            <button 
              className={activeFilter === 'SEE ALL' ? 'filter-tab active' : 'filter-tab'}
              onClick={() => setActiveFilter('SEE ALL')}
            >
              {isHomeLayout ? 'All' : t('products.seeAll')}
            </button>
          </div>
        </div>
        {!isHomeLayout && (
        <div className="game-filters">
          {games.map((game, index) => {
            const iconPath = getGameIcon(game);
            return (
              <button key={index} className="game-filter-btn">
                {iconPath && <img src={iconPath} alt={game} className="game-filter-icon" />}
                <span>{game}</span>
              </button>
            );
          })}
        </div>
        )}
        <div className="products-grid">
          {loading ? (
            <>
              {Array.from({ length: skeletonCount }, (_, i) => i + 1).map((i) => (
                <ProductCardSkeleton key={i} />
              ))}
            </>
          ) : hasLoadedOnce && products.length === 0 ? (
            <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>
              {t('products.noFeatured') || 'No featured products available'}
            </div>
          ) : products.length > 0 ? (
            products.map((product) => (
              <ProductCard
                key={product.id}
                product={product}
                isSelected={selectedProduct === product.id}
                showSelection={false}
              />
            ))
          ) : null}
        </div>
      </div>
    </section>
  );
};

export default FeaturedProducts;

